import type { EmployeeRewardDiscipline } from "services/EmployeeRewardDiscipline/types";
import type { EmployeeWorkAccident } from "services/EmployeeWorkAccident/types";

export interface ReportReconcile {
  reconcileSalaryAndInsurance: ReconcileSalaryAndInsurance[];
  reconcileAllowance: unknown[];
  reconcileOverviewEmp: ReconcileOverviewEmp[];
  reconcileDependant: ReconcileDependant[];
  reconcileDependantSummary: ReconcileDependantSummary[];
}
interface ReconcileDependantSummary {
  lastMonth: number;
  thisMonth: number;
}

interface ReconcileDependant {
  empId: number;
  empCode: string;
  empFullname: string;
  thisMonth: number;
  lastMonth: number;
}

interface ReconcileOverviewEmp {
  lastMonth: string;
  thisMonth: string;
  newHired: string;
  resigned: string;
}

interface ReconcileSalaryAndInsurance {
  year: number;
  month: number;
  [key: string]: string | number;
}

export interface ReportReconcileRequest {
  month?: number | null;
  year?: number | null;
  templateId?: string | null;
  templatePeriod?: number | null;
  isExport?: boolean | null;
}

export interface ReportOverTimeRequest {
  page?: number | null;
  limit?: number | null;
  months?: number[] | null;
  years?: number[] | null;
  departmentIds?: number[] | null;
  branchIds?: number[] | null;
  employmentTypes?: string[] | null;
  titleCodes?: string[] | null;
  workLevelCodes?: string[] | null;
  workLevelIds?: number[] | null;
}

export interface ReportPayrollSummaryRequest {
  month?: number | null;
  year?: number | null;
  templateId?: string | null;
  templatePeriod?: string | null;
  cycleId?: string | null;
  empDeptIds?: number[] | null;
  empOfficeIds?: number[] | null;
  empTypeCodes?: string[] | null;
  empTitleCodes?: string[] | null;
  empWorkLevels?: number[] | null;
}

export interface ReportSHUIContributionRequest {
  page?: number | null;
  limit?: number | null;
  month?: number | null;
  year?: number | null;
  deductionTypes?: string[] | null;
  empDeptIds?: number[] | null;
  empOfficeIds?: number[] | null;
  empTypeCodes?: string[] | null;
  empTitleCodes?: string[] | null;
  empWorkLevels?: number[] | null;
}

export interface ReportPITSummaryRequest {
  page?: number | null;
  limit?: number | null;
  month?: number | null;
  year?: number | null;
  empDeptIds?: number[] | null;
  empOfficeIds?: number[] | null;
  empTypeCodes?: string[] | null;
  empTitleCodes?: string[] | null;
  empWorkLevels?: number[] | null;
}

export interface ReportPayrollSummaryItem {
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  createdBy?: string;
  updatedBy?: string;
  ownerList?: unknown;
  orgId: number;
  month: number;
  year: number;
  templateId: string;
  empId: number;
  empFullname: string;
  empStartDate: string;
  empEndDate?: string;
  empTypeCode: string;
  noOfDependent: number;
  empDeptName: string;
  empOfficeName: string;
  empTitleName: string;
  empGenderName: string;
  empBirthday: string;
  empEmail: string;
  empPhonenumber: string;
  empBankAccountno: string;
  empBankAccountname: string;
  empBankName: string;
  empWorkLevel: string;
  empWorkLevelName: string;
  empWorkLevelNativeName: string;
  empContractTypeName: string;
  empContractTypeNativeName: string;
  empTaxCode: string;
  empJoinDate: string;
  insRateEmp: string;
  payCycleStartdate: number;
  payCycleEnddate: number;
  pitFmlDeductionIndividual: number;
  pitFmlDeductionDependent: number;
  payTemplatecode: string;
  pitRate: string;
  compSalaryAdv: string;
  compSalaryActual: string;
  compSalaryLeaveActual: string;
  compTotalSalary: string;
  compSalaryShui: string;
  insTotalSiEmp: string;
  insTotalHiEmp: string;
  insTotalUiEmp: string;
  insTotalSiCom: string;
  insTotalHiCom: string;
  insTotalUiCom: string;
  insTotalInsuranceEmp: string;
  insTotalInsuranceComp: string;
  insTotalUnionComp: string;
  insTotalUnionEmp: string;
  compTotalAllowanceTaxable: string;
  compTotalAllowanceExempt: string;
  compTotalAllowance: string;
  compOthIncome: string;
  compTotalIncomeBeforeTax: string;
  compTotalIncomeExempt: string;
  compTotalIncomeTaxable: string;
  pitFmlTotalDeductionDependent: string;
  pitFmlTotalDeduction: string;
  compTotalIncomeCalPit: string;
  pitTotalLiability: string;
  compTotalIncomeAfterTax: string;
  compTotalDeductionAfterTax: string;
  compTotalTakehome: string;
  expTotalCompanyPay: string;
  taActualWorkingdays: string;
  taOtWeekdaysHoursTaxable: string;
  taOtWeekdaysHoursExempt: string;
  taOtWeekendHoursTaxable: string;
  taOtWeekendHoursExempt: string;
  taOtPublicHolidaysHoursTaxable: string;
  taOtPublicHolidaysHoursExempt: string;
  compOtTotalSalaryTaxable: string;
  compOtTotalSalaryExempt: string;
  compOtTotalSalary: string;
  taTotalUnpaidLeaves: string;
  taTotalPaidLeaves: string;
  taTotalActualWorkingdays: string;
  compTotalAdditionAfterTax: string;
  uniqueStatus: string;
  empStatus: string;
  empCode: string;
  compTotalActualSalary: string;
  empDeptId: number;
  empTitleCode: string;
  status: number;
  payStandworkingDay: string;
  empAllowances: EmpAllowance[];
  empEarnings: EmpEarnings[];
  empCompanyDeductions: EmpEarnings[];
  empEmployeeDeductions: EmpEarnings[];
  empOfficeId?: number;
}

interface EmpEarnings {
  id: string;
  name: string;
  amount: number;
  nativeName: string;
}

interface EmpAllowance {
  id: string;
  name: string;
  amount: number;
  nativeName: string;
}

export interface ReportAttendance {
  month: number;
  year: number;
  employeeCode: string;
  fullName: string;
  standardWorkDays: number;
  totalHolidays: number;
  paidLeaves: number;
  unpaidLeaves: number;
  totalWorkDays: number;
  workShiftNames: string[];
  employee: Employee;
}

interface RefLeaveType {
  id: string;
  code: string;
  name: string;
  orgId: string;
  isPaid: boolean;
}

export interface ReportLeave {
  month: number;
  year: number;
  employeeCode: string;
  fullName: string;
  date: string;
  code: string;
  name: string;
  duration: number;
  value: number;
  reason: string;
  employee?: Employee;
  refLeaveType: RefLeaveType;
}

export interface ReportOverTime {
  month: number;
  year: number;
  employeeCode: string;
  fullName: string;
  date: string;
  fromTime: string;
  toTime: string;
  dateType: number;
  otHours: number;
  otRate: number;
  otRateCode?: string;
  description: string;
  employee: Employee;
}

interface Employee {
  id: number;
  code: string;
  email: string;
  title: string;
  branch: Branch;
  userId: number;
  manager: Manager;
  fullName: string;
  titleCode: string;
  workLevel: WorkLevel;
  department: Department;
  workLevelId: number;
  departmentId: number;
  employmentType: string;
  lastWorkingDate?: string;
  employmentStatus: string;
  firstWorkingDate: string;
}

interface Department {
  id: number;
  code: string;
  name: string;
  paths: number[];
}

interface WorkLevel {
  id: number;
  code: string;
  name: string;
  nativeName: string;
}

interface Manager {
  id: number;
  code: string;
  email: string;
  title: string;
  fullName: string;
  titleCode: string;
}

interface Branch {
  id: number;
  code: string;
  name: string;
  provinceId: number;
}

export interface ReportOverTimeResponse extends RestAPIMetaResponse {
  items: Array<ReportOverTime>;
}

export interface ReportAttendanceResponse extends RestAPIMetaResponse {
  items: Array<ReportAttendance>;
}

export interface ReportLeaveResponse extends RestAPIMetaResponse {
  items: Array<ReportLeave>;
}

export interface ReportPayrollSummaryHeader {
  UI_COMPANY: string;
  UI_EMPLOYEE: string;
  HI_COMPANY: string;
  HI_EMPLOYEE: string;
  SI_COMPANY: string;
  SI_EMPLOYEE: string;
}

export interface ReportPayrollSummaryResponse extends RestAPIMetaResponse {
  headers: ReportPayrollSummaryHeader;
  items: Array<ReportPayrollSummaryItem>;
}

export type ReportReconcileResponse = ReportReconcile;

// Report SHUI Contribution
export enum ReportSHUIContributionDeductionType {
  SOCIAL_INSURANCE = "SI",
  HEALTH_INSURANCE = "HI",
  UNEMPLOYEE_INSURANCE = "UI",
}

export enum ReportSHUIContributionItemType {
  ALLOWANCE = "ALLOWANCE",
  EARNING = "EARNING",
  DEDUCTION = "DEDUCTION",
}

export enum ReportSHUIContributionObjectType {
  COMPANY = 1,
  EMPLOYEE = 3,
}

export interface ReportSHUIContributionItem {
  id: string;
  empId: number;
  empCode: string;
  empFullname: string;
  empEndDate?: string;
  empDeptName: string;
  empOfficeName?: string;
  empTitleName: string;
  empJoinDate: string;
  compSalaryShui: number;
  insTotalSiEmp: number;
  insTotalHiEmp: number;
  insTotalUiEmp: number;
  insTotalSiCom: number;
  insTotalHiCom: number;
  insTotalUiCom: number;
  insTotalInsuranceEmp: number;
  insTotalInsuranceComp: number;
  compAllowanceShui: number;
  empShuiStartMonth?: string;
  empShuiEndMonth?: string;
  insTotalSiEmpRate: number;
  insTotalHiEmpRate: number;
  insTotalUiEmpRate: number;
  insTotalSiComRate: number;
  insTotalHiComRate: number;
  insTotalUiComRate: number;

  insTotalUnionEmpRate: number;
  insTotalUnionEmp: number;

  insTotalUnionCompRate: number;
  insTotalUnionComp: number;
  empShuiBookNumber?: string;
  payrollDetail: (PayrollDetail | PayrollDetail2)[];
}

interface PayrollDetail2 {
  id: string;
  code: string;
  name: string;
  nativeName: string;
  value: string;
  type: string;
  kind?: string;
  pit: boolean;
  insurance: boolean;
  rate: number;
  kindObjectType: ReportSHUIContributionObjectType;
}

interface PayrollDetail {
  id: string;
  code: string;
  name: string;
  nativeName: string;
  value: string;
  type: string;
  kind?: string;
  pit: boolean;
  insurance: boolean;
  rate: number;
  kindObjectType: ReportSHUIContributionObjectType;
}

export interface ReportSHUIContributionResponse extends RestAPIMetaResponse {
  items: Array<ReportSHUIContributionItem>;
}

export interface ReportPITSummaryItem {
  id: string;
  empId: number;
  empCode: string;
  empFullname: string;
  noOfDependent: number;
  empDeptName: string;
  empTaxCode?: string;
  pitFmlDeductionIndividual: number;
  compTotalAllowanceTaxable: number;
  compTotalAllowanceExempt: number;
  compTotalIncomeExempt: number;
  pitFmlTotalDeduction: number;
  compTotalIncomeCalPit: number;
  pitTotalLiability: number;
  compOtTotalSalaryTaxable: number;
  compOtTotalSalaryExempt: number;
  compTotalSalaryWithPitTick: number;
  compTotalSalaryWithoutPitTick: number;
  isAuthorizeTaxFinalization?: boolean;
  isExpat?: boolean;
  empIdCardNumber?: number;
  sumPitTotalLiability: number;
}

export interface ReportPITSummaryResponse extends RestAPIMetaResponse {
  items: Array<ReportPITSummaryItem>;
}

export interface ReportOneStream {
  legalEntityCode: string;
  costCenter: string;
  glCode: string;
  empId: number;
  empCode: string;
  amount: string;
  month: number;
  year: number;
  currency: string;
  description: string;
}

export interface EmployeeProfile {
  company: Company;
  profile: Profile;
  idCard: IdCard;
  contact: Contact;
  insurance: Insurance;
  educations: Education[];
  ta: Ta[];
  workingHistories: WorkingHistory[];
  rewardDisciplinary: EmployeeRewardDiscipline[];
  workAccident: EmployeeWorkAccident[];
}

interface WorkingHistory {
  month: number;
  year: number;
  empTypeCode: string;
  empDeptName: string;
  empOfficeName: string;
  empTitleName: string;
  compTotalTakehome: number;
  seniority: number;
  order: number;
}

interface Ta {
  year: number;
  holidays: number;
  otHours: number;
  unpaidLeaves: number;
  paidLeaves: number;
  totalPaidLeaves: number;
  workingdays: number;
}

interface Education {
  id: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: null;
  status: number;
  createdBy: CreatedBy;
  updatedBy: CreatedBy;
  courseName: string;
  courseContent: string;
  fromDate: string;
  toDate: string;
  timeOfTraining: number;
  formOfTraining: string;
  methodOfTraining: string;
  trainingCenter: string;
  levelOfTraining: null;
  finalResult: string;
  attachments: string[];
  coursePrice: number;
  note: string;
  employeeId: number;
  organizationId: number;
}

interface Insurance {
  id: number;
  createdAt: string;
  updatedAt: string;
  status: number;
  createdBy: CreatedBy;
  updatedBy: CreatedBy;
  siBookNumber: string;
  siStartMonth: number;
  siEndMonth: number;
  siAgencies: null;
  siStatus: string;
  siRefUrl: null;
  hiCardNumber: string;
  hiHospitalCode: string;
  hiHospitalName: string;
  hiRefUrl: null;
  empRegion: null;
  empPosition: string;
  empPositionStartDate: null;
  empPositionEndDate: null;
  hazardousStartDate: null;
  hazardousEndDate: null;
  siMethodCode: string;
  siMethodName: string;
  siNote: string;
  countryId: number;
  ethnicityId: null;
  employeeId: number;
  organizationId: number;
  country: Country;
  ethnicity: null;
}

interface Contact {
  id: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: null;
  status: number;
  createdBy: CreatedBy;
  updatedBy: CreatedBy;
  addressType: string;
  phoneCode: null;
  phoneNumber: string;
  address: string;
  countryId: number;
  provinceId: number;
  districtId: number;
  wardId: number;
  employeeId: number;
  organizationId: number;
}

interface IdCard {
  id: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: null;
  status: number;
  createdBy: CreatedBy;
  updatedBy: CreatedBy;
  idType: string;
  effectiveDate: string;
  idNumber: string;
  dateOfIssue: string;
  placeOfIssue: string;
  expiryDate: string;
  frontOfImage: null;
  backOfImage: null;
  employeeId: number;
  organizationId: number;
}

interface Profile {
  id: number;
  code: string;
  fullName: string;
  dateOfBirth: string;
  gender: string;
  title: string;
  titleCode: string;
  firstWorkingDate: string;
  lastWorkingDate: null;
  contractNo: string;
  contractStartDate: null;
  organizationId: number;
  department: Department;
  contractType: Country;
  educationLevel: string;
  occupationalSkillLevel: string;
  employmentStatus: string;
}

interface Department {
  id: number;
  name: string;
  code: string;
  paths: number[];
  deptCode: null;
}

interface Company {
  id: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: null;
  status: number;
  createdBy: CreatedBy;
  updatedBy: CreatedBy;
  name: string;
  businessName: string;
  code: string;
  description: null;
  logo: string;
  taxCode: string;
  contactEmail: string;
  contactPhone: string;
  postalCode: string;
  address: string;
  latitude: null;
  longitude: null;
  tenantId: number;
  countryId: number;
  provinceId: number;
  districtId: number;
  wardId: number;
  settings: Settings;
  tenant: Tenant;
  country: Country;
  province: Country;
  district: Country;
  ward: Country;
  noOfEmployees: number;
}

interface Country {
  id: number;
  code: string;
  name: string;
  nativeName: string;
}

interface Tenant {
  id: number;
  code: string;
  name: string;
}

interface Settings {
  themeName: string;
  theme: Theme;
  logo: string;
  shortLogo: string;
  copyrightText: string;
  contrastText: string;
}

interface Theme {
  "25": string;
  "50": string;
  "100": string;
  "200": string;
  "300": string;
  "400": string;
  "500": string;
  "600": string;
  "700": string;
  "800": string;
  "900": string;
  "950": string;
}

interface CreatedBy {
  userId: number;
  fullName: string;
  employeeId: number;
}

export interface Diligence {
  empId: number;
  empCode: string;
  empFullname: string;
  empTitleName: string;
  empStartDate: string;
  empDeptId: number;
  empDeptName: string;
  time: string;
  amount: string;
  stt: number;
}

export interface ReportLaborManagementBookRequest {
  page?: number | null;
  limit?: number | null;
  departmentId?: number | null;
  departmentIds?: number[] | null;
  employeeIds?: number[] | null;
  keyword?: string | null;
  status?: number | null;
}

export interface LaborManagementBookCompany {
  id: number;
  name: string;
  businessName: string;
  code: string;
  taxCode: string | null;
  address: string;
  tenant?: {
    id: number;
    code: string;
    name: string;
  };
  country?: {
    id: number;
    code: string;
    name: string;
    nativeName: string;
  };
  province?: {
    id: number;
    code: string;
    nativeName: string;
    name: string;
  };
  district?: {
    id: number;
    code: string;
    nativeName: string;
    name: string;
  };
  ward?: {
    id: number;
    code: string;
    nativeName: string;
    name: string;
  };
  noOfEmployees: number;
  totalWorkingEmployees: number;
  femaleEmployees: number;
  participatingSocialInsuranceEmployees: number;
  contractExpiringIn30DaysEmployees: number;
}

export interface LaborManagementBookDepartment {
  id: number;
  name: string;
  code: string;
  paths: number[];
  deptCode: string | null;
}

export interface LaborManagementBookContractType {
  id: number;
  name: string;
  nativeName: string;
  code: string;
}

export interface LaborManagementBookProfile {
  id: number;
  code: string;
  fullName: string;
  dateOfBirth: string | null;
  gender: string | null;
  title: string | null;
  titleCode: string | null;
  firstWorkingDate: string | null;
  lastWorkingDate: string | null;
  employmentStatus: string | null;
  contractNo: string | null;
  contractStartDate: string | null;
  contractEndDate: string | null;
  organizationId: number;
  department: LaborManagementBookDepartment | null;
  contractType: LaborManagementBookContractType | null;
  educationLevel: string | null;
  occupationalSkillLevel: string | null;
}

export interface LaborManagementBookIdCard {
  id: number;
  idType: string;
  effectiveDate: string | null;
  idNumber: string;
  dateOfIssue: string | null;
  placeOfIssue: string | null;
  expiryDate: string | null;
}

export interface LaborManagementBookInsurance {
  id: number;
  siBookNumber: string | null;
  siStartMonth: string | null;
  siEndMonth: string | null;
}

export interface LaborManagementBookEducation {
  id?: number;
  courseName?: string | null;
  courseContent?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
  timeOfTraining?: number | null;
  formOfTraining?: string | null;
  methodOfTraining?: string | null;
  trainingCenter?: string | null;
  levelOfTraining?: string | null;
  finalResult?: string | null;
  note?: string | null;
  employeeId?: number;
  [key: string]: unknown;
}

export interface LaborManagementBookTA {
  year: number;
  holidays: number;
  otHours: number;
  unpaidLeaves: number;
  paidLeaves: number;
  totalPaidLeaves: number;
  workingdays: number;
}

export interface LaborManagementBookWorkingHistory {
  month: number;
  year: number;
  empId: number;
  empTypeCode: string;
  empDeptName: string;
  empOfficeName: string;
  empTitleName: string;
  compTotalTakehome: number;
}

export interface LaborManagementBookContact {
  id?: number;
  phoneNumber?: string | null;
  email?: string | null;
  address?: string | null;
  [key: string]: unknown;
}

export interface LaborManagementBookItem {
  profile: LaborManagementBookProfile;
  idCard: LaborManagementBookIdCard | null;
  contact: LaborManagementBookContact | null;
  insurance: LaborManagementBookInsurance | null;
  educations: LaborManagementBookEducation[];
  ta: LaborManagementBookTA[];
  workingHistories: LaborManagementBookWorkingHistory[];
  rewardDisciplinary: EmployeeRewardDiscipline[];
  workAccident: EmployeeWorkAccident[];
  [key: string]: unknown;
}

export interface ReportLaborManagementBookResponse extends RestAPIMetaResponse {
  company: LaborManagementBookCompany;
  items: Array<LaborManagementBookItem>;
}
